import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Plus, Minus, Package, Calendar, Truck, ShoppingBag } from 'lucide-react';
import Layout from '../Layout';
import { API_BASE_URL } from '../../helpers/ApiSettings';
import { CartManager } from '../../helpers/cart';
import { SaleCartManager } from '../../helpers/SaleCartManager';
import { UnifiedCartManager, UNIFIED_CART_UPDATE_EVENT } from '../../helpers/UnifiedCartManager';
import { showToast } from '../../helpers/customToast';
import { useDeliveryThreshold } from '../../helpers/useDeliveryThreshold';
import '../../css/cart.css';

const UnifiedCartPage = () => {
  const [rentalItems, setRentalItems] = useState([]);
  const [salesItems, setSalesItems] = useState([]);
  const [rentalTotal, setRentalTotal] = useState(0);
  const [salesTotal, setSalesTotal] = useState(0);
  const [deposit, setDeposit] = useState(0);
  const [isValidating, setIsValidating] = useState(true);
  const freeDeliveryThreshold = useDeliveryThreshold();

  useEffect(() => {
    UnifiedCartManager.setupListeners();
    loadCartData();

    // Listen for cart updates
    const handleCartUpdate = () => {
      loadCartData();
    };
    window.addEventListener(UNIFIED_CART_UPDATE_EVENT, handleCartUpdate);

    return () => {
      window.removeEventListener(UNIFIED_CART_UPDATE_EVENT, handleCartUpdate);
    };
  }, []);

  const loadCartData = async () => {
    setIsValidating(true);
    try {
      // Load and validate rental items
      const { validatedCart, hasRemovedItems } = await CartManager.validateAndRemoveUnavailableItems();

      if (hasRemovedItems && validatedCart.length === 0) {
        showToast('Certains articles ont été retirés du panier car ils ne sont plus disponibles.', 'warning');
      } else if (hasRemovedItems) {
        showToast('Certains articles indisponibles ont été retirés de votre panier.', 'warning');
      }

      setRentalItems(validatedCart);
      const rentalPrice = validatedCart.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
      setRentalTotal(rentalPrice);
      setDeposit(CartManager.getDeposit());

      // Load sales items
      const salesCart = SaleCartManager.getCart();
      setSalesItems(salesCart);
      const salesPrice = salesCart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      setSalesTotal(salesPrice);
    } catch (error) {
      console.error('Error loading cart data:', error);
      showToast('Une erreur est survenue lors du chargement du panier.', 'error');
    } finally {
      setIsValidating(false);
    }
  };

  // Rental cart handlers
  const handleRemoveRentalItem = (itemId, startDate, endDate) => {
    try {
      CartManager.removeFromCart(itemId, startDate, endDate);
      loadCartData();
      showToast('Article retiré du panier.', 'info');
    } catch (error) {
      console.error('Error removing rental item:', error);
      showToast('Une erreur est survenue, merci de réessayer.', 'error');
    }
  };

  // Sales cart handlers
  const handleRemoveSaleItem = (productDocumentId) => {
    try {
      SaleCartManager.removeFromCart(productDocumentId);
      loadCartData();
      showToast('Article retiré du panier.', 'info');
    } catch (error) {
      console.error('Error removing sale item:', error);
      showToast('Une erreur est survenue, merci de réessayer.', 'error');
    }
  };

  const handleUpdateSaleQuantity = (productDocumentId, newQuantity) => {
    try {
      SaleCartManager.updateQuantity(productDocumentId, newQuantity);
      loadCartData();
    } catch (error) {
      console.error('Error updating quantity:', error);
      showToast('Une erreur est survenue lors de la mise à jour.', 'error');
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  const getDeliveryPrice = () => {
    return SaleCartManager.isEligibleForFreeDelivery() ? 0 : 5.99;
  };

  const getSalesTotalWithDelivery = () => {
    return salesTotal + getDeliveryPrice();
  };

  if (isValidating) {
    return (
      <Layout>
        <div className="container my-5">
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p className="mt-2">Chargement du panier...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const isEmpty = rentalItems.length === 0 && salesItems.length === 0;

  return (
    <Layout>
      <div className="container my-5 shopping-cart">
        <div className="row mb-4">
          <div className="col-12">
            <h1>Votre panier</h1>
          </div>
        </div>

        {isEmpty ? (
          <div className="row">
            <div className="col-12">
              <div className="text-center py-5">
                <Package size={64} className="text-muted mb-3" />
                <h3>Votre panier est vide</h3>
                <p className="text-muted mb-4">Découvrez nos services de location et notre boutique</p>
                <div className="d-flex gap-3 justify-content-center">
                  <Link to="/services/location" className="btn btn-outline-primary">
                    Voir les locations
                  </Link>
                  <Link to="/vente" className="btn btn-primary">
                    Voir la boutique
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="row">
            <div className="col-12">
              {/* Sales Items Section */}
              {salesItems.length > 0 && (
                <div className="card mb-4">
                  <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">
                      <ShoppingBag size={20} className="me-2" style={{ display: 'inline' }} />
                      Articles à acheter
                    </h5>
                  </div>
                  <div className="card-body p-0">
                    {salesItems.map((item, index) => {
                      const isBulkApplied = SaleCartManager.isBulkPriceApplied(item.product, item.quantity);
                      return (
                        <div key={`sale-${item.product.documentId}-${index}`} className="border-bottom p-3">
                          <div className="row align-items-center">
                            <div className="col-md-2">
                              <img
                                src={API_BASE_URL + item.product.images[0].url}
                                alt={item.product.title}
                                className="img-fluid rounded"
                                style={{ maxHeight: '80px' }}
                              />
                            </div>

                            <div className="col-md-4">
                              <h6 className="mb-1">
                                <Link
                                  to={`/vente/produit/${item.product.documentId}`}
                                  className="text-decoration-none"
                                >
                                  {item.product.title}
                                </Link>
                              </h6>
                              {item.product.brand && (
                                <div>
                                  <small className="text-muted">{item.product.brand}</small>
                                </div>
                              )}
                              {isBulkApplied && (
                                <small className="text-success">
                                  ✓ Prix dégressif appliqué (à partir de {item.product.bulk_quantity})
                                </small>
                              )}
                            </div>

                            <div className="col-md-2">
                              <div className="fw-bold">{item.price}€</div>
                              <small className="text-muted">par unité</small>
                              {isBulkApplied && item.product.unit_price !== item.product.bulk_price && (
                                <div>
                                  <small className="text-decoration-line-through text-muted">
                                    {item.product.unit_price}€
                                  </small>
                                </div>
                              )}
                            </div>

                            <div className="col-md-3">
                              <div className="input-group input-group-sm">
                                <button
                                  className="btn btn-outline-secondary"
                                  type="button"
                                  onClick={() => handleUpdateSaleQuantity(
                                    item.product.documentId,
                                    item.quantity - 1
                                  )}
                                  disabled={item.quantity <= 1}
                                >
                                  <Minus size={14} />
                                </button>
                                <input
                                  type="number"
                                  className="form-control text-center"
                                  value={item.quantity}
                                  onChange={(e) => {
                                    const newQty = Math.max(1, parseInt(e.target.value) || 1);
                                    handleUpdateSaleQuantity(item.product.documentId, newQty);
                                  }}
                                  min="1"
                                />
                                <button
                                  className="btn btn-outline-secondary"
                                  type="button"
                                  onClick={() => handleUpdateSaleQuantity(
                                    item.product.documentId,
                                    item.quantity + 1
                                  )}
                                >
                                  <Plus size={14} />
                                </button>
                              </div>
                              <small className="text-muted">
                                Sous-total: {(item.price * item.quantity).toFixed(2)}€
                              </small>
                            </div>

                            <div className="col-md-1 text-end">
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleRemoveSaleItem(item.product.documentId)}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="card-footer">
                    <div className="row">
                      <div className="col-md-8">
                        <div className="d-flex justify-content-between mb-2">
                          <span>Sous-total ({SaleCartManager.getItemCount()} articles)</span>
                          <span className="fw-bold">{salesTotal.toFixed(2)}€</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span>
                            <Truck size={16} className="me-1" />
                            Livraison
                          </span>
                          <span className={SaleCartManager.isEligibleForFreeDelivery() ? 'text-success fw-bold' : ''}>
                            {SaleCartManager.isEligibleForFreeDelivery() ? 'Gratuite' : '5,99€'}
                          </span>
                        </div>
                        {!SaleCartManager.isEligibleForFreeDelivery() && (
                          <small className="text-muted">
                            Livraison gratuite à partir de {freeDeliveryThreshold}€ (encore {(freeDeliveryThreshold - salesTotal).toFixed(2)}€)
                          </small>
                        )}
                      </div>
                      <div className="col-md-4 text-end">
                        <div className="mb-2">
                          <strong>Total: {getSalesTotalWithDelivery().toFixed(2)}€</strong>
                        </div>
                        <Link to="/vente/checkout" className="btn btn-primary w-100">
                          Commander ces articles
                        </Link>
                        <Link to="/vente" className="btn btn-outline-primary w-100 mt-2">
                          Continuer mes achats
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Rental Items Section */}
              {rentalItems.length > 0 && (
                <div className="card mb-4">
                  <div className="card-header bg-success text-white">
                    <h5 className="mb-0">
                      <Calendar size={20} className="me-2" style={{ display: 'inline' }} />
                      Articles à louer
                    </h5>
                  </div>
                  <div className="card-body p-0">
                    <ul className="list-group list-group-flush">
                      {rentalItems.map((item, index) => (
                        <li key={`rental-${index}`} className="list-group-item">
                          <div className="row align-items-center">
                            <div className="col-md-2">
                              <img
                                src={API_BASE_URL + item.product.images[0].url}
                                alt={item.product.titre}
                                className="img-fluid rounded"
                              />
                            </div>
                            <div className="col-md-5">
                              <h6>
                                <Link to={`/produit/${item.product.documentId}`} className="text-decoration-none">
                                  {item.product.titre}
                                </Link>
                              </h6>
                              <div className="mt-2">
                                <small className="text-muted me-3">
                                  <Calendar size={16} className="me-1" />
                                  {formatDate(item.startDate)} - {formatDate(item.endDate)}
                                </small>
                              </div>
                              <div className="mt-1">
                                <small className="text-muted">
                                  Quantité: {item.quantity || 1}
                                </small>
                              </div>
                            </div>
                            <div className="col-md-3 text-md-end">
                              <h6>{(item.price * (item.quantity || 1)).toFixed(2)}€</h6>
                            </div>
                            <div className="col-md-2 text-md-end">
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleRemoveRentalItem(item.id, item.startDate, item.endDate)}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="card-footer">
                    <div className="row">
                      <div className="col-md-8">
                        <div className="d-flex justify-content-between mb-2">
                          <span>Caution</span>
                          <span className="fw-bold">{deposit.toFixed(2)}€</span>
                        </div>
                        <small className="text-muted">La caution sera remboursée après retour du matériel</small>
                      </div>
                      <div className="col-md-4 text-end">
                        <div className="mb-2">
                          <strong>Total: {rentalTotal.toFixed(2)}€</strong>
                        </div>
                        <Link to="/checkout" className="btn btn-success w-100">
                          Réserver ces articles
                        </Link>
                        <Link to="/services/location" className="btn btn-outline-success w-100 mt-2">
                          Continuer la location
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default UnifiedCartPage;
